/**
 * Table action button configurations and actions column for the claims dashboard.
 */
import React from 'react';
import { RenderTableActionButtons } from '../../../../commons/tableActionButton/tableActionButton';
import { claimRoleTypes, CLAIM_STATUS_RANK } from './claimRoleConfig';
import {
  isClaimEditableForClient,
  isClaimRevocableForClient
} from '../../claimsUtils';
import { CLAIM_WORKFLOW_ACTIONS } from '../../constants/claimWorkflowActions';

const viewIcon = require('../../../../../assets/images/view.svg');
const infoIcon = require('../../../../../assets/images/info-black.svg');
const editIcon = require('../../../../../assets/images/ic_mode_edit_black_24px.svg');
const revokeIcon = require('../../../../../assets/images/rotate.svg');
const validateRcaIcon = require('../../../../../assets/images/file_upload.svg');
const rejectIcon = require('../../../../../assets/images/red-cross.svg');
const approveIcon = require('../../../../../assets/images/success.svg');
const forwardIcon = require('../../../../../assets/images/right_arrow.svg');
const assignIcon = require('../../../../../assets/images/assign.svg');

let currentClaimsDashboardRoleRef = { value: 'CLAIM_DESK' };
let claimActionHandlersRef = { value: {} };

export const setActionRefs = (roleRef, handlersRef) => {
  currentClaimsDashboardRoleRef = roleRef;
  claimActionHandlersRef = handlersRef;
};

const getClaimStatus = rowData => {
  const rawData = (rowData && rowData.rawData) || {};
  const status = rawData.status || rowData.status || '';
  return status.toUpperCase().replace(/\s+/g, '_');
};

const isReworkClaim = rowData => {
  const rawData = (rowData && rowData.rawData) || {};
  return Boolean(
    rawData.isRework ||
      rawData.is_rework ||
      rawData.rework ||
      rawData.reworkFlag
  );
};

const isCreatorEditable = rowData => {
  const status = getClaimStatus(rowData);
  return isClaimEditableForClient(status, isReworkClaim(rowData));
};

const canShowRoleAction = rowData => {
  const claimRole = currentClaimsDashboardRoleRef.value;
  const status = getClaimStatus(rowData);

  switch (claimRole) {
    case claimRoleTypes.GROUND_DEPS:
      return false; // Handled by a specific icon button now
    case claimRoleTypes.ZOOM_CLM:
      return false; // Handled by a specific icon button now
    case claimRoleTypes.HQ_DEPS:
      return false; // Removed as per request
    case claimRoleTypes.CLAIM_DESK:
      return false; // Handled by a specific icon button now
    case claimRoleTypes.HOD_DESK:
      return (
        ['CLAIM_PENDING', 'ACKNOWLEDGEMENT_COMPLETED'].indexOf(status) !== -1
      );
    case claimRoleTypes.OPS_HOD:
      return (
        [
          'CLAIM_PENDING',
          'ACKNOWLEDGEMENT_COMPLETED',
          'OPS_HOD_APPROVAL_PENDING'
        ].indexOf(status) !== -1
      );
    case claimRoleTypes.SALES_HOD:
      return (
        [
          'CLAIM_PENDING',
          'ACKNOWLEDGEMENT_COMPLETED',
          'SALES_HOD_APPROVAL_PENDING'
        ].indexOf(status) !== -1
      );

    case claimRoleTypes.FINANCE:
      return ['SETTLEMENT_PENDING', 'CLAIM_PENDING'].indexOf(status) !== -1;
    default:
      return false;
  }
};

const getRoleActionLabel = rowData => {
  const claimRole = currentClaimsDashboardRoleRef.value;
  const status = getClaimStatus(rowData);

  switch (claimRole) {
    case claimRoleTypes.GROUND_DEPS:
      return 'Submit RCA';
    case claimRoleTypes.ZOOM_CLM:
      return 'Validate RCA';
    case claimRoleTypes.HQ_DEPS:
      return status === 'UNDER_VERIFICATION' ? 'Review' : 'Action';
    case claimRoleTypes.CLAIM_DESK:
      return 'Approve';
    case claimRoleTypes.HOD_DESK:
      return status === 'ACKNOWLEDGEMENT_COMPLETED'
        ? 'Generate COF'
        : 'Approve';
    case claimRoleTypes.OPS_HOD:
    case claimRoleTypes.SALES_HOD:
    case claimRoleTypes.FINANCE:
      return 'Approve';
    default:
      return '';
  }
};
const isApproveDisabled = rowData => {
  const claimRole = currentClaimsDashboardRoleRef.value;
  const status = getClaimStatus(rowData);

  if (claimRole === claimRoleTypes.SAM_KAM) {
    return true;
  }

  if (
    claimRole === claimRoleTypes.CLIENT_USER ||
    claimRole === claimRoleTypes.RP_USER
  ) {
    return (
      ['CREATED', 'ACKNOWLEDGEMENT_PENDING', 'APPROVED'].indexOf(status) === -1
    );
  }
  if (claimRole === claimRoleTypes.CLAIM_DESK) {
    return !['INVESTIGATED', 'ACKNOWLEDGEMENT_COMPLETED'].includes(status);
  }
  if (claimRole === claimRoleTypes.HQ_DEPS) {
    return status !== 'ACKNOWLEDGEMENT_COMPLETED';
  }
  if (claimRole === claimRoleTypes.HOD_DESK) {
    return !['CLAIM_PENDING', 'APPROVAL_PENDING'].includes(status);
  }
  if (claimRole === claimRoleTypes.OPS_HOD) {
    return ![
      'CLAIM_PENDING',
      'APPROVAL_PENDING',
      'OPS_HOD_APPROVAL_PENDING'
    ].includes(status);
  }
  if (claimRole === claimRoleTypes.SALES_HOD) {
    return ![
      'CLAIM_PENDING',
      'APPROVAL_PENDING',
      'SALES_HOD_APPROVAL_PENDING'
    ].includes(status);
  }
  if (claimRole === claimRoleTypes.FINANCE) {
    return !['APPROVAL_PENDING', 'SETTLEMENT_PENDING'].includes(status);
  }
  return false;
};

const isApproveClickable = rowData => {
  const claimRole = currentClaimsDashboardRoleRef.value;
  const status = getClaimStatus(rowData);

  if (claimRole === claimRoleTypes.SAM_KAM) {
    return false;
  }

  if (status === 'REVOKED' || status === 'REJECTED') {
    return false;
  }

  if (!isApproveDisabled(rowData)) {
    return true;
  }

  const allowedReadOnlyRoles = [
    claimRoleTypes.CLAIM_DESK,
    claimRoleTypes.HOD_DESK,
    claimRoleTypes.OPS_HOD,
    claimRoleTypes.SALES_HOD,
    claimRoleTypes.FINANCE,
    claimRoleTypes.HQ_DEPS
  ];

  if (allowedReadOnlyRoles.includes(claimRole)) {
    const rank = CLAIM_STATUS_RANK[status] || 0;
    return rank >= CLAIM_STATUS_RANK.ACKNOWLEDGEMENT_PENDING;
  }

  return false;
};

export const claimsDashboardTableActionConfig = {
  currentRole: claimRoleTypes.CLAIM_DESK,
  buttons: [
    {
      label: 'view',
      iconOnly: true,
      iconRef: viewIcon,
      onClick: rowData => {
        if (claimActionHandlersRef.value.openViewModal) {
          claimActionHandlersRef.value.openViewModal(rowData);
        }
      },
      style: {
        width: '40px',
        height: '40px',
        margin: '2%'
      }
    },
    {
      label: 'Validate RCA',
      iconOnly: true,
      iconRef: validateRcaIcon,
      hide: () => {
        const claimRole = currentClaimsDashboardRoleRef.value;
        const allowedRoles = [
          claimRoleTypes.ZOOM_CLM,
          claimRoleTypes.HQ_DEPS,
          claimRoleTypes.CLAIM_DESK,
          claimRoleTypes.HOD_DESK,
          claimRoleTypes.OPS_HOD,
          claimRoleTypes.SALES_HOD,
          claimRoleTypes.FINANCE
        ];
        return !allowedRoles.includes(claimRole);
      },
      disabledFn: rowData => {
        const claimRole = currentClaimsDashboardRoleRef.value;
        const status = getClaimStatus(rowData);
        if (status === 'REVOKED') return true;

        if (claimRole === claimRoleTypes.ZOOM_CLM) {
          return status !== 'RCA_SUBMITTED';
        }
        const rank = CLAIM_STATUS_RANK[status] || 0;
        return rank < CLAIM_STATUS_RANK.RCA_SUBMITTED;
      },
      onClick: rowData => {
        if (claimActionHandlersRef.value.openRoleAction) {
          claimActionHandlersRef.value.openRoleAction(
            CLAIM_WORKFLOW_ACTIONS.VALIDATE_RCA,
            rowData
          );
        }
      },
      style: {
        width: '40px',
        height: '40px',
        margin: '2%'
      }
    },
    {
      label: 'Approve',
      iconOnly: true,
      iconRef: approveIcon,
      iconCb: rowData => (
        <svg viewBox="0 0 96 96" width="100%" height="100%">
          <path
            fill={isApproveDisabled(rowData) ? '#9CA3AF' : '#24AA38'}
            d="M41.786 66L25 49.205l4.734-4.736 12.052 12.025L67.266 31 72 35.77z"
          />
        </svg>
      ),
      hide: () => {
        const claimRole = currentClaimsDashboardRoleRef.value;
        const disallowedRoles = [
          claimRoleTypes.HQ_DEPS,
          claimRoleTypes.GROUND_DEPS,
          claimRoleTypes.ZOOM_CLM
        ];
        return disallowedRoles.includes(claimRole);
      },
      disabledFn: rowData => !isApproveClickable(rowData),
      onClick: rowData => {
        if (claimActionHandlersRef.value.openRoleAction) {
          const status = getClaimStatus(rowData);
          const claimRole = currentClaimsDashboardRoleRef.value;
          const isClient =
            claimRole === claimRoleTypes.CLIENT_USER ||
            claimRole === claimRoleTypes.RP_USER;
          const action =
            ['ACKNOWLEDGEMENT_PENDING', 'APPROVED'].indexOf(status) !== -1 &&
            isClient
              ? CLAIM_WORKFLOW_ACTIONS.ACKNOWLEDGE
              : CLAIM_WORKFLOW_ACTIONS.APPROVE_CLAIM;
          claimActionHandlersRef.value.openRoleAction(action, rowData);
        }
      },
      style: {
        width: '40px',
        height: '40px',
        margin: '2%'
      }
    },
    {
      label: 'Revoke',
      iconOnly: true,
      iconRef: revokeIcon,
      hide: () => {
        const claimRole = currentClaimsDashboardRoleRef.value;
        const allowedRoles = [
          claimRoleTypes.CLIENT_USER,
          claimRoleTypes.RP_USER,
          claimRoleTypes.SAM_KAM
        ];
        return !allowedRoles.includes(claimRole);
      },
      disabledFn: rowData => {
        return !isClaimRevocableForClient(getClaimStatus(rowData));
      },
      onClick: rowData => {
        if (claimActionHandlersRef.value.openRoleAction) {
          claimActionHandlersRef.value.openRoleAction(
            CLAIM_WORKFLOW_ACTIONS.REVOKE,
            rowData
          );
        }
      },
      style: {
        width: '40px',
        height: '40px',
        margin: '2%'
      }
    },

    {
      label: 'Edit',
      iconOnly: true,
      iconRef: editIcon,
      hide: () => {
        const claimRole = currentClaimsDashboardRoleRef.value;
        const allowedRoles = [
          claimRoleTypes.CLIENT_USER,
          claimRoleTypes.RP_USER,
          claimRoleTypes.SAM_KAM
        ];
        return !allowedRoles.includes(claimRole);
      },
      disabledFn: rowData => {
        return !isCreatorEditable(rowData);
      },
      onClick: rowData => {
        if (claimActionHandlersRef.value.openRoleAction) {
          claimActionHandlersRef.value.openRoleAction(
            CLAIM_WORKFLOW_ACTIONS.EDIT,
            rowData
          );
        }
      },
      style: {
        width: '40px',
        height: '40px',
        margin: '2%'
      }
    },
    {
      label: 'Submit RCA',
      iconOnly: true,
      iconRef: validateRcaIcon,
      hide: rowData => {
        const claimRole = currentClaimsDashboardRoleRef.value;
        const status = getClaimStatus(rowData);
        if (claimRole !== claimRoleTypes.GROUND_DEPS) return true;
        if (status === 'REVOKED') return true;
        const rank = CLAIM_STATUS_RANK[status] || 0;
        return rank < CLAIM_STATUS_RANK.RCA_PENDING;
      },
      onClick: rowData => {
        if (claimActionHandlersRef.value.openRoleAction) {
          claimActionHandlersRef.value.openRoleAction(
            CLAIM_WORKFLOW_ACTIONS.SUBMIT_RCA,
            rowData
          );
        }
      },
      style: {
        width: '40px',
        height: '40px',
        margin: '2%'
      }
    },
    {
      label: 'Verify Claim Request',
      iconOnly: true,
      iconRef: validateRcaIcon,
      hide: rowData => {
        const claimRole = currentClaimsDashboardRoleRef.value;
        const status = getClaimStatus(rowData);
        if (claimRole === claimRoleTypes.HQ_DEPS) {
          return status !== 'UNDER_VERIFICATION';
        }
        return true;
      },
      onClick: rowData => {
        if (claimActionHandlersRef.value.openRoleAction) {
          claimActionHandlersRef.value.openRoleAction(
            CLAIM_WORKFLOW_ACTIONS.VERIFY_CLAIM,
            rowData
          );
        }
      },
      style: {
        width: '40px',
        height: '40px',
        margin: '2%'
      }
    },

    {
      label: 'info',
      iconOnly: true,
      iconRef: infoIcon,
      onClick: rowData => {
        if (claimActionHandlersRef.value.openInfoModal) {
          claimActionHandlersRef.value.openInfoModal(rowData);
        }
      },
      style: {
        width: '40px',
        height: '40px',
        margin: '2%'
      }
    },
    {
      label: 'Reassign User',
      iconOnly: true,
      iconRef: assignIcon,
      hide: rowData => {
        const claimRole = currentClaimsDashboardRoleRef.value;
        const status = getClaimStatus(rowData);
        if (claimRole !== claimRoleTypes.HQ_DEPS) return true;
        if (status !== 'UNDER_INVESTIGATION' && status !== 'RCA_SUBMITTED')
          return true;
        return false;
      },
      onClick: rowData => {
        if (claimActionHandlersRef.value.openRoleAction) {
          claimActionHandlersRef.value.openRoleAction(
            CLAIM_WORKFLOW_ACTIONS.REASSIGN,
            rowData
          );
        }
      },
      style: {
        width: '40px',
        height: '40px',
        margin: '2%'
      }
    },
    {
      label: 'Forward',
      iconOnly: true,
      iconRef: forwardIcon,
      hide: () => {
        return true;
      },
      onClick: () => {},
      style: {
        width: '40px',
        height: '40px',
        margin: '2%'
      }
    },
    {
      label: 'Reject',
      iconOnly: true,
      iconRef: rejectIcon,
      iconCb: () => (
        <svg viewBox="0 0 512 512" width="100%" height="100%">
          <path
            fill="#f44336"
            d="m350.273438 320.105469c8.339843 8.34375 8.339843 21.824219 0 30.167969-4.160157 4.160156-9.621094 6.25-15.085938 6.25-5.460938 0-10.921875-2.089844-15.082031-6.25l-64.105469-64.109376-64.105469 64.109376c-4.160156 4.160156-9.621093 6.25-15.082031 6.25-5.464844 0-10.925781-2.089844-15.085938-6.25-8.339843-8.34375-8.339843-21.824219 0-30.167969l64.109376-64.105469-64.109376-64.105469c-8.339843-8.34375-8.339843-21.824219 0-30.167969 8.34375-8.339843 21.824219-8.339843 30.167969 0l64.105469 64.109376 64.105469-64.109376c8.34375-8.339843 21.824219-8.339843 30.167969 0 8.339843 8.34375 8.339843 21.824219 0 30.167969l-64.109376 64.105469zm0 0"
          />
        </svg>
      ),
      hide: () => {
        const claimRole = currentClaimsDashboardRoleRef.value;
        const allowedRoles = [
          claimRoleTypes.HQ_DEPS,
          claimRoleTypes.CLAIM_DESK
        ];
        return !allowedRoles.includes(claimRole);
      },
      disabledFn: rowData => {
        const claimRole = currentClaimsDashboardRoleRef.value;
        const status = getClaimStatus(rowData);
        if (status === 'REVOKED') return true;

        if (claimRole === claimRoleTypes.HQ_DEPS) {
          return !['RCA_APPROVED', 'UNDER_VERIFICATION'].includes(status);
        }
        if (claimRole === claimRoleTypes.CLAIM_DESK) {
          return !['INVESTIGATED', 'ACKNOWLEDGEMENT_COMPLETED'].includes(
            status
          );
        }
        return true;
      },
      onClick: rowData => {
        if (claimActionHandlersRef.value.openRoleAction) {
          claimActionHandlersRef.value.openRoleAction(
            CLAIM_WORKFLOW_ACTIONS.REJECT_CLAIM,
            rowData
          );
        }
      },
      style: {
        width: '40px',
        height: '40px',
        margin: '2%'
      }
    },
    {
      label: 'Acknowledge',
      labelOnly: true,
      hide: () => {
        return true;
      },
      onClick: rowData => {
        if (claimActionHandlersRef.value.openRoleAction) {
          claimActionHandlersRef.value.openRoleAction(
            CLAIM_WORKFLOW_ACTIONS.ACKNOWLEDGE,
            rowData
          );
        }
      }
    },
    {
      label: rowData => getRoleActionLabel(rowData),
      labelOnly: true,
      hide: rowData =>
        !canShowRoleAction(rowData) ||
        getRoleActionLabel(rowData) === 'Approve',
      onClick: rowData => {
        if (claimActionHandlersRef.value.openRoleAction) {
          claimActionHandlersRef.value.openRoleAction(
            CLAIM_WORKFLOW_ACTIONS.ROLE_ACTION,
            rowData
          );
        }
      }
    }
  ]
};

export const actionsColumn = {
  Header: () => <div className="claims-inline-352">Actions</div>,

  headerClassName: 'flex-25',
  className: 'flex-25',
  sortable: false,
  Cell: obj => {
    const rowData = obj.original || {};
    const claimRole = currentClaimsDashboardRoleRef.value;

    if (claimRole === claimRoleTypes.GROUND_DEPS) {
      const groundDepsButtons = [
        {
          label: 'Submit RCA',
          iconOnly: true,
          iconRef: validateRcaIcon,
          disabledFn: r => {
            const status = getClaimStatus(r);
            return status !== 'UNDER_INVESTIGATION' && status !== 'RCA_PENDING';
          },
          onClick: r => {
            if (claimActionHandlersRef.value.openRoleAction) {
              claimActionHandlersRef.value.openRoleAction(
                CLAIM_WORKFLOW_ACTIONS.SUBMIT_RCA,
                r
              );
            }
          },
          style: { width: '40px', height: '40px', margin: '2%' }
        },
        {
          label: 'view',
          iconOnly: true,
          iconRef: viewIcon,
          disabledFn: () => false,
          onClick: r => {
            if (claimActionHandlersRef.value.openViewModal) {
              claimActionHandlersRef.value.openViewModal(r);
            }
          },
          style: { width: '40px', height: '40px', margin: '2%' }
        },
        {
          label: 'info',
          iconOnly: true,
          iconRef: infoIcon,
          disabledFn: () => false,
          onClick: r => {
            if (claimActionHandlersRef.value.openInfoModal) {
              claimActionHandlersRef.value.openInfoModal(r);
            }
          },
          style: { width: '40px', height: '40px', margin: '2%' }
        }
      ];

      return (
        <div className="claims-inline-353">
          <RenderTableActionButtons
            rowData={rowData}
            buttonConfig={groundDepsButtons}
          />
        </div>
      );
    }

    if (claimRole === claimRoleTypes.HQ_DEPS) {
      const hqDepsButtons = [
        {
          label: 'Approve',
          iconOnly: true,
          iconCb: r => (
            <svg viewBox="0 0 96 96" width="100%" height="100%">
              <path
                fill={
                  getClaimStatus(r) !== 'UNDER_VERIFICATION'
                    ? '#9CA3AF'
                    : '#24AA38'
                }
                d="M41.786 66L25 49.205l4.734-4.736 12.052 12.025L67.266 31 72 35.77z"
              />
            </svg>
          ),
          disabledFn: r => getClaimStatus(r) !== 'UNDER_VERIFICATION',
          onClick: r => {
            if (claimActionHandlersRef.value.openRoleAction) {
              claimActionHandlersRef.value.openRoleAction(
                CLAIM_WORKFLOW_ACTIONS.VERIFY_CLAIM,
                r
              );
            }
          },
          style: { width: '40px', height: '40px', margin: '2%' }
        },
        {
          label: 'Reject',
          iconOnly: true,
          iconCb: r => (
            <svg viewBox="0 0 512 512" width="100%" height="100%">
              <path
                fill={
                  !['RCA_APPROVED', 'UNDER_VERIFICATION'].includes(
                    getClaimStatus(r)
                  )
                    ? '#9CA3AF'
                    : '#f44336'
                }
                d="m350.273438 320.105469c8.339843 8.34375 8.339843 21.824219 0 30.167969-4.160157 4.160156-9.621094 6.25-15.085938 6.25-5.460938 0-10.921875-2.089844-15.082031-6.25l-64.105469-64.109376-64.105469 64.109376c-4.160156 4.160156-9.621093 6.25-15.082031 6.25-5.464844 0-10.925781-2.089844-15.085938-6.25-8.339843-8.34375-8.339843-21.824219 0-30.167969l64.109376-64.105469-64.109376-64.105469c-8.339843-8.34375-8.339843-21.824219 0-30.167969 8.34375-8.339843 21.824219-8.339843 30.167969 0l64.105469 64.109376 64.105469-64.109376c8.34375-8.339843 21.824219-8.339843 30.167969 0 8.339843 8.34375 8.339843 21.824219 0 30.167969l-64.109376 64.105469zm0 0"
              />
            </svg>
          ),
          disabledFn: r =>
            !['RCA_APPROVED', 'UNDER_VERIFICATION'].includes(getClaimStatus(r)),
          onClick: r => {
            if (claimActionHandlersRef.value.openRoleAction) {
              claimActionHandlersRef.value.openRoleAction(
                CLAIM_WORKFLOW_ACTIONS.REJECT_CLAIM,
                r
              );
            }
          },
          style: { width: '40px', height: '40px', margin: '2%' }
        },
        {
          label: 'Validate RCA',
          iconOnly: true,
          iconRef: validateRcaIcon,
          disabledFn: r => {
            const status = getClaimStatus(r);
            if (status === 'REVOKED') return true;
            const rank = CLAIM_STATUS_RANK[status] || 0;
            return rank < CLAIM_STATUS_RANK.RCA_SUBMITTED;
          },
          onClick: r => {
            if (claimActionHandlersRef.value.openRoleAction) {
              claimActionHandlersRef.value.openRoleAction(
                CLAIM_WORKFLOW_ACTIONS.VALIDATE_RCA,
                r
              );
            }
          },
          style: { width: '40px', height: '40px', margin: '2%' }
        },
        {
          label: 'view',
          iconOnly: true,
          iconRef: viewIcon,
          disabledFn: () => false,
          onClick: r => {
            if (claimActionHandlersRef.value.openViewModal) {
              claimActionHandlersRef.value.openViewModal(r);
            }
          },
          style: { width: '40px', height: '40px', margin: '2%' }
        },
        {
          label: 'Reassign User',
          iconOnly: true,
          iconRef: assignIcon,
          disabledFn: r => {
            const status = getClaimStatus(r);
            return (
              status !== 'UNDER_INVESTIGATION' && status !== 'RCA_SUBMITTED'
            );
          },
          onClick: r => {
            if (claimActionHandlersRef.value.openRoleAction) {
              claimActionHandlersRef.value.openRoleAction(
                CLAIM_WORKFLOW_ACTIONS.REASSIGN,
                r
              );
            }
          },
          style: { width: '40px', height: '40px', margin: '2%' }
        },
        {
          label: 'info',
          iconOnly: true,
          iconRef: infoIcon,
          disabledFn: () => false,
          onClick: r => {
            if (claimActionHandlersRef.value.openInfoModal) {
              claimActionHandlersRef.value.openInfoModal(r);
            }
          },
          style: { width: '40px', height: '40px', margin: '2%' }
        }
      ];

      return (
        <div className="claims-inline-353">
          <RenderTableActionButtons
            rowData={rowData}
            buttonConfig={hqDepsButtons}
          />
        </div>
      );
    }

    const allButtons = claimsDashboardTableActionConfig.buttons;
    const activeButtons = allButtons.filter(button => {
      if (button.hide && button.hide(rowData)) {
        return false;
      }
      return true;
    });

    const priorityMap = {
      Approve: 1,
      Reject: 2,
      Revoke: 2,
      'Submit RCA': 3,
      'Validate RCA': 3,
      'Verify Claim Request': 3,
      view: 4,
      Edit: 5,
      'Reassign User': 6,
      info: 7
    };

    const getPriority = button => {
      const label =
        typeof button.label === 'function'
          ? button.label(rowData)
          : button.label;
      return priorityMap[label] || 99;
    };

    const renderedButtons = [...activeButtons].sort(
      (a, b) => getPriority(a) - getPriority(b)
    );

    return (
      <div className="claims-inline-353">
        <RenderTableActionButtons
          rowData={rowData}
          buttonConfig={renderedButtons}
        />
      </div>
    );
  }
};
